/**
 * Token management utility
 * Handles storing and retrieving authentication tokens
 */
export class TokenManager {
  // Token storage keys
  static ACCESS_TOKEN_KEY = 'skai1AccessToken';
  static REFRESH_TOKEN_KEY = 'skai1RefreshToken';

  /**
   * Set access token
   * @param {string} token - Access token
   */
  static setAccessToken(token) {
    if (token) {
      localStorage.setItem(this.ACCESS_TOKEN_KEY, token);
    }
  }

  /**
   * Get access token
   * @returns {string|null} Access token
   */
  static getAccessToken() {
    return localStorage.getItem(this.ACCESS_TOKEN_KEY);
  }

  /**
   * Set refresh token
   * @param {string} token - Refresh token
   */
  static setRefreshToken(token) {
    if (token) {
      localStorage.setItem(this.REFRESH_TOKEN_KEY, token);
    }
  }

  /**
   * Get refresh token
   * @returns {string|null} Refresh token
   */
  static getRefreshToken() {
    return localStorage.getItem(this.REFRESH_TOKEN_KEY);
  }

  /**
   * Set both access and refresh tokens
   * @param {string} accessToken - Access token
   * @param {string} refreshToken - Refresh token (optional)
   */
  static setTokens(accessToken, refreshToken = null) {
    this.setAccessToken(accessToken);
    if (refreshToken) {
      this.setRefreshToken(refreshToken);
    }
  }

  /**
   * Clear all tokens
   */
  static clearTokens() {
    localStorage.removeItem(this.ACCESS_TOKEN_KEY);
    localStorage.removeItem(this.REFRESH_TOKEN_KEY);
  }

  /**
   * Check if user is authenticated
   * @returns {boolean} True if access token exists
   */
  static isAuthenticated() {
    return !!this.getAccessToken();
  }

  /**
   * Parse JWT token to get payload (without verification)
   * @param {string} token - JWT token
   * @returns {object|null} Token payload
   */
  static parseToken(token) {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );
      return JSON.parse(jsonPayload);
    } catch (error) {
      console.error('Error parsing token:', error);
      return null;
    }
  }

  /**
   * Check if token is expired
   * @param {string} token - JWT token
   * @returns {boolean} True if token is expired
   */
  static isTokenExpired(token) {
    if (!token) return true;
    
    const payload = this.parseToken(token);
    if (!payload || !payload.exp) return true;
    
    const currentTime = Math.floor(Date.now() / 1000);
    return payload.exp < currentTime;
  }

  /**
   * Get token expiration date
   * @param {string} token - JWT token
   * @returns {Date|null} Expiration date
   */
  static getTokenExpiration(token) {
    const payload = this.parseToken(token);
    if (!payload || !payload.exp) return null;
    
    return new Date(payload.exp * 1000);
  }
}

export default TokenManager;
